/**
 * Severity-aware color mapping for chart widgets.
 *
 * When a widget's chartBy or groupBy column references a severity/status column,
 * this utility maps the series/item names to their canonical status_color from
 * the Centerity API.
 *
 * Recognized column names and their value mapping:
 *   severity      → display_name  (e.g. "Critical", "Ok")
 *   state         → display_name  (common alias for severity in custom queries)
 *   severity_id   → id            (e.g. "CRITICAL", "OK")
 *   status        → id            (e.g. "CRITICAL", "OK", "UP")
 *   status_id     → id            (e.g. "CRITICAL", "OK")
 */

/** Columns whose values are status display_name (title-case). */
const DISPLAY_NAME_COLUMNS = new Set(['severity', 'state']);

/** Columns whose values are status id (uppercase). */
const ID_COLUMNS = new Set(['severity_id', 'status', 'status_id']);

const stripTablePrefix = (col) =>
  col.includes('.') ? col.split('.').pop() : col;

/**
 * Returns the correct lookup sub-map (byDisplayName or byId) based on the column name,
 * or null if the column is not severity-related.
 */
const getSeverityColorLookup = (columnName, statusColorMap) => {
  if (!columnName || !statusColorMap) return null;
  const stripped = stripTablePrefix(columnName).toLowerCase();
  if (DISPLAY_NAME_COLUMNS.has(stripped)) return statusColorMap.byDisplayName;
  if (ID_COLUMNS.has(stripped)) return statusColorMap.byId;
  return null;
};

const applySeriesColors = (data, lookup) => {
  if (!data.series || !lookup) return data;
  return {
    ...data,
    series: data.series.map(s => ({
      ...s,
      color: lookup[s.name] || s.color,
    })),
  };
};

const applyCategoryColors = (data, lookup) => {
  if (!data.categories || !lookup) return data;
  return {
    ...data,
    categoryColors: data.categories.map(cat => lookup[cat] || null),
  };
};

const applyItemColors = (data, lookup) => {
  if (!Array.isArray(data) || !lookup) return data;
  return data.map(item => ({
    ...item,
    color: lookup[item.name] || item.color,
  }));
};

/**
 * Enrich widget data with severity-based colors when applicable.
 *
 * @param {Object|Array} data         – Transformed widget data
 * @param {string}       widgetType   – e.g. "grouped-bar", "pie", "bar"
 * @param {Object}       widgetConfig – Full widget config (contains dataSources)
 * @param {Object}       statusColorMap – { byDisplayName: {…}, byId: {…} }
 * @returns {Object|Array} Data with colors injected, or the original data unchanged
 */
export const applySeverityColors = (data, widgetType, widgetConfig, statusColorMap) => {
  if (!data || !statusColorMap) return data;

  const dataSources = widgetConfig?.dataSources || [];
  const relevantSource = dataSources.find(ds => ds.type === 'table' || ds.type === 'custom_query');
  const config = relevantSource?.config || {};

  const groupByLookup = getSeverityColorLookup(config.groupBy, statusColorMap);
  const chartByLookup = getSeverityColorLookup(config.chartBy, statusColorMap);

  switch (widgetType) {
    case 'grouped-bar':
    case 'stacked-bar':
    case 'stacked-line': {
      if (groupByLookup && data.series) return applySeriesColors(data, groupByLookup);
      if (chartByLookup && data.categories) return applyCategoryColors(data, chartByLookup);
      return data;
    }

    case 'bar':
    case 'line': {
      if (data.series) {
        const lookup = chartByLookup || groupByLookup;
        if (lookup) return applySeriesColors(data, lookup);
      }
      if (data.categories && data.values && chartByLookup) {
        return applyCategoryColors(data, chartByLookup);
      }
      return data;
    }

    case 'pie':
    case 'donut':
    case 'treemap': {
      if (chartByLookup) return applyItemColors(data, chartByLookup);
      return data;
    }

    default:
      return data;
  }
};
